import React, { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AuthModal from "./AuthModal";

export const REASONS = [
  ["spam", "Spam"], ["harassment", "Harassment / bullying"], ["inappropriate", "Inappropriate content"],
  ["scam", "Scam / fraud"], ["other", "Other"],
];

export default function ReportButton({ type, id, ownerId, label = "Report", className = "", iconOnly = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [auth, setAuth] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (user && ownerId && user.user_id === ownerId) return null;
  const tid = `report-${type}-${id}`;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reports", { target_type: type, target_id: id, reason, note });
      toast.success("Thanks — our moderators will review this report.");
      setOpen(false); setReason(""); setNote("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      if (e.response?.status === 409) setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); user ? setOpen(true) : setAuth(true); }} data-testid={tid} title="Report"
        className={`inline-flex items-center gap-1 hover:text-amber-400 transition-colors ${className}`}>
        <Flag size={12} />{!iconOnly && <span>{label}</span>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0d1117] border-white/10 text-white max-w-md" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Flag size={16} className="text-amber-400" /> Report violation</DialogTitle>
            <DialogDescription className="text-white/50">Tell us what's wrong. Reports are private — the member won't see who reported them.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2" role="radiogroup">
            {REASONS.map(([v, l]) => (
              <button key={v} type="button" onClick={() => setReason(v)} data-testid={`report-reason-${v}`} role="radio" aria-checked={reason === v}
                className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${reason === v ? "border-amber-500/60 bg-amber-500/15 text-amber-200" : "border-white/10 text-white/70 hover:border-white/25"}`}>{l}</button>
            ))}
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={3} placeholder="Add details (optional)" data-testid="report-note"
            className="w-full px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-500/50" />
          <button onClick={submit} disabled={!reason || busy} data-testid="report-submit" className="w-full py-2.5 rounded-full bg-amber-500 text-black font-bold disabled:opacity-40">
            {busy ? <Loader2 size={15} className="animate-spin mx-auto" /> : "Send report"}
          </button>
        </DialogContent>
      </Dialog>
      <AuthModal open={auth} onClose={() => setAuth(false)} />
    </>
  );
}
